import { useState } from 'react';
import { ArrowForward, Check, LockOutlined, OpenInNew } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { AgreementReview } from './AgreementReview';
import type { AgreementsSetupProps } from './AgreementsSetup.types';

/** Same component can be embedded in the customer or existing scoped admin host. */
export function AgreementsSetup(props: AgreementsSetupProps) {
  // Scope / preparation changes discard local acceptance and signature drafts.
  return <AgreementsSetupContent key={props.preparationKey} {...props} />;
}

function AgreementsSetupContent({
  practiceName,
  packages,
  selectedPackageId,
  onSelectPackage,
  onSign,
  onContinueToIntake,
  context = { kind: 'provider' },
  canSign = false,
  loading = false,
  previewOnly = false,
  showStageRail = true,
  kickoffUrl = 'https://calendly.com/d/d3zg-tpb-b84/implementation-kickoff',
  initialSection = 'services',
  servicesContent,
  servicesSummary,
  servicesPending = false,
}: AgreementsSetupProps) {
  const [section, setSection] = useState(initialSection);
  const [documentIndex, setDocumentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const submitSignature = async (documentId: string, signature: string) => {
    setSubmitting(true);
    try {
      await onSign(documentId, signature);
    } finally {
      setSubmitting(false);
    }
  };
  const selected = packages.find(option => option.id === selectedPackageId);
  const documents = selected?.documents ?? [];
  const signedCount = documents.filter(document => document.signed).length;
  const complete = documents.length > 0 && signedCount === documents.length;
  const readOnly = submitting || signedCount > 0 || context.kind === 'admin' || !canSign;
  const activeDocument = documents[documentIndex] ?? documents[0];
  const navigation = [
    {
      id: 'services' as const,
      title: 'Your services',
      detail: servicesSummary ?? selected?.title ?? 'Choose your services',
      done: !!selected,
    },
    {
      id: 'agreements' as const,
      title: 'Agreements',
      detail: documents.length ? `${signedCount} of ${documents.length} signed` : 'Review and sign',
      done: complete,
    },
    {
      id: 'kickoff' as const,
      title: 'Implementation kickoff',
      detail: 'Schedule a call',
      done: false,
    },
  ];
  return (
    <Box sx={{ bgcolor: '#f7f7fb', minHeight: '100%', color: 'text.primary' }}>
      {previewOnly && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Interactive preview · Synthetic terms and signatures. No agreement is saved.
        </Alert>
      )}
      <Box
        sx={{
          bgcolor: 'background.paper',
          px: { xs: 2, md: 5 },
          py: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {showStageRail ? 'Get billing-ready' : 'Agreements & setup'}
          </Typography>
          <Typography color="text.secondary">
            {showStageRail
              ? 'Four steps, then your first claim.'
              : 'Review your services and agreements.'}
          </Typography>
        </Stack>
        {showStageRail && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 3,
              mt: 4,
            }}
          >
            {[
              'Agreements & setup',
              'Intake form',
              'JotPsych verification',
              'Payer connections',
            ].map((label, index) => (
              <Stack key={label} direction="row" spacing={1.5}>
                <Box
                  sx={{
                    bgcolor: index === 0 ? 'primary.main' : 'action.hover',
                    color: index === 0 ? 'primary.contrastText' : 'text.disabled',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {index === 0 ? (
                    complete ? (
                      <Check fontSize="small" />
                    ) : (
                      1
                    )
                  ) : index === 1 && complete ? (
                    2
                  ) : (
                    <LockOutlined sx={{ fontSize: 16 }} />
                  )}
                </Box>
                <Box>
                  <Typography
                    sx={{ fontWeight: 700 }}
                    color={index ? 'text.secondary' : 'text.primary'}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {index === 0
                      ? complete
                        ? 'Complete'
                        : 'In progress'
                      : index === 1 && complete
                        ? 'Ready to start'
                        : 'Locked'}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
          gap: { xs: 3, md: 5 },
          p: { xs: 2, md: 5 },
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        <Box component="nav" aria-label="Agreements and setup">
          <Typography variant="overline" color="text.secondary">
            Before your intake
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {complete
              ? 'All set. You can re-read anything here any time.'
              : 'Review your services, sign your agreements, and book your kickoff.'}
          </Typography>
          <Stack spacing={1}>
            {navigation.map(item => (
              <Button
                key={item.id}
                disabled={
                  submitting ||
                  servicesPending ||
                  (Boolean(servicesContent) && item.id === 'agreements')
                }
                color="inherit"
                aria-current={section === item.id ? 'step' : undefined}
                onClick={() => setSection(item.id)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  p: 2,
                  border: '1px solid',
                  borderColor: section === item.id ? 'divider' : 'transparent',
                  bgcolor: section === item.id ? 'background.paper' : 'transparent',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    mr: 2,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: item.done
                      ? 'success.main'
                      : section === item.id
                        ? 'primary.main'
                        : 'background.paper',
                    color: 'white',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  {item.done && <Check sx={{ fontSize: 18 }} />}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.detail}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Stack>
        </Box>
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {practiceName}
            {context.kind === 'admin' ? ' · Staff view' : ''}
          </Typography>
          {loading ? (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress size={28} />
              <Typography sx={{ mt: 2 }}>Loading your prepared agreements…</Typography>
            </Paper>
          ) : (
            <>
              {section === 'services' && servicesContent}
              {section === 'services' && !servicesContent && (
                <>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Which services are you signing up for?
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      Your selection fills in the order form and determines the agreements you
                      review.
                    </Typography>
                  </Box>
                  {readOnly && (
                    <Alert severity="info">
                      {signedCount > 0
                        ? 'Your signed service selection is read-only. Contact your JotPsych team to discuss changes.'
                        : context.kind === 'admin'
                          ? 'Staff can review the prepared services. The practice administrator signs from their own account.'
                          : 'A practice administrator can select services and sign these agreements.'}
                    </Alert>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    {packages.map(option => (
                      <Card
                        key={option.id}
                        variant="outlined"
                        sx={{
                          borderWidth: 2,
                          borderColor: option.id === selectedPackageId ? 'primary.main' : 'divider',
                          borderRadius: 2,
                        }}
                      >
                        <CardActionArea
                          disabled={readOnly}
                          onClick={() => {
                            setDocumentIndex(0);
                            onSelectPackage(option.id);
                          }}
                          aria-pressed={option.id === selectedPackageId}
                          sx={{ p: 3, height: '100%', alignItems: 'start' }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {option.title}
                          </Typography>
                          <Typography color="text.secondary" sx={{ mt: 1 }}>
                            {option.description}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                            {option.services.map(service => (
                              <Chip key={service} label={service} size="small" />
                            ))}
                          </Stack>
                        </CardActionArea>
                      </Card>
                    ))}
                  </Box>
                  {selected && (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        You’ll sign
                      </Typography>
                      <Typography>
                        {documents.map(document => document.title).join(' · ')}
                      </Typography>
                    </Paper>
                  )}
                  <Box>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      disabled={!selected}
                      onClick={() => setSection('agreements')}
                    >
                      Continue to agreements
                    </Button>
                  </Box>
                </>
              )}
              {section === 'agreements' && (
                <>
                  {documents.length > 0 ? (
                    <>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        {documents.map((document, index) => (
                          <Button
                            key={document.id}
                            disabled={submitting}
                            variant={activeDocument?.id === document.id ? 'contained' : 'outlined'}
                            onClick={() => setDocumentIndex(index)}
                            startIcon={document.signed ? <Check /> : undefined}
                          >
                            {document.title}
                          </Button>
                        ))}
                      </Stack>
                      {activeDocument && (
                        <AgreementReview
                          key={`${selectedPackageId}-${activeDocument.id}`}
                          document={activeDocument}
                          canSign={canSign && context.kind === 'provider'}
                          onSign={submitSignature}
                        />
                      )}
                    </>
                  ) : (
                    <Alert severity="info">
                      Choose your services to see the agreements for your practice.
                    </Alert>
                  )}
                </>
              )}
              {section === 'kickoff' && (
                <>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Schedule your implementation kickoff
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      A call with your account manager. Walk through your payers and what the intake
                      will ask.
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={3}
                      sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Implementation kickoff
                        </Typography>
                        <Typography color="text.secondary">
                          Bring whoever runs billing and administers your current EHR.
                        </Typography>
                      </Box>
                      <Button
                        href={kickoffUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        endIcon={<OpenInNew />}
                        sx={{ flexShrink: 0 }}
                      >
                        Pick a time
                      </Button>
                    </Stack>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      What we’ll cover
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Typography>Your payers and which ones need enrollment</Typography>
                      <Typography>Your transition needs and who on our side owns each</Typography>
                      <Typography>What the intake asks, so you can gather it once</Typography>
                    </Stack>
                  </Paper>
                  <Typography variant="body2" color="text.secondary">
                    Scheduling does not block your intake. You can return to this link any time.
                  </Typography>
                </>
              )}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  pt: 1,
                  alignItems: { sm: 'center' },
                  display: servicesContent ? 'none' : undefined,
                }}
              >
                <Button
                  variant={complete ? 'contained' : 'outlined'}
                  endIcon={<ArrowForward />}
                  disabled={!complete}
                  onClick={onContinueToIntake}
                >
                  Continue to intake form
                </Button>
                {!complete && (
                  <Typography variant="body2" color="text.secondary">
                    {documents.length
                      ? `${documents.length - signedCount} agreement${documents.length - signedCount === 1 ? '' : 's'} still unsigned`
                      : 'Choose your services first'}
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
